/**
 * Страница "Настройки → AI-индикатор" (фича 031, US2).
 *
 * Показывает текущее состояние и позволяет сбросить счётчики активной
 * модели. Сброс — soft (история не удаляется, audit-запись в
 * ai_usage_resets).
 */

import { ResetConfirmDialog } from "@/components/ai-usage/ResetConfirmDialog";
import { AiResetModelMismatchError, resetAiCounters } from "@/services/api";
import { useAiStatus } from "@/hooks/use-ai-status";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export function AiUsageSettings() {
	const { data, isLoading } = useAiStatus();
	const queryClient = useQueryClient();
	const [showConfirm, setShowConfirm] = useState(false);

	const resetMutation = useMutation({
		mutationFn: (modelName: string) => resetAiCounters(modelName),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["ai-status"] });
			setShowConfirm(false);
			toast.success("Счётчики сброшены");
		},
		onError: (err) => {
			if (err instanceof AiResetModelMismatchError) {
				toast.error(
					`Модель сменилась во время диалога (была "${err.expected}", стала "${err.actual}"). Повторите сброс.`,
				);
				queryClient.invalidateQueries({ queryKey: ["ai-status"] });
				setShowConfirm(false);
			} else {
				toast.error(err instanceof Error ? err.message : "Не удалось сбросить счётчики");
			}
		},
	});

	if (isLoading) {
		return (
			<div className="text-sm text-slate-500">
				Загрузка состояния AI-индикатора...
			</div>
		);
	}

	const modelName = data?.modelName ?? null;
	const promptTokens = data?.aggregated?.promptTokens ?? 0;
	const completionTokens = data?.aggregated?.completionTokens ?? 0;
	const sessionsCount = data?.aggregated?.sessionsCount ?? 0;
	const lastResetAt = data?.aggregated?.lastResetAt ?? null;

	const canReset = modelName !== null && (promptTokens > 0 || completionTokens > 0);

	return (
		<section className="mx-auto max-w-3xl space-y-6">
			<header>
				<h1 className="text-2xl font-bold text-slate-900">AI-индикатор</h1>
				<p className="mt-1 text-sm text-slate-500">
					Учёт расхода токенов по текущей AI-модели и управление счётчиками.
				</p>
			</header>

			<div className="rounded-lg border border-slate-200 bg-white p-5">
				<h2 className="text-lg font-semibold text-slate-900">Текущая модель</h2>
				<dl className="mt-3 grid grid-cols-2 gap-y-3 text-sm">
					<dt className="text-slate-500">Имя модели</dt>
					<dd className="font-mono text-slate-900">{modelName ?? "не определена"}</dd>

					<dt className="text-slate-500">Входящие токены</dt>
					<dd className="font-mono text-slate-900">
						{promptTokens.toLocaleString("ru-RU")}
					</dd>

					<dt className="text-slate-500">Исходящие токены</dt>
					<dd className="font-mono text-slate-900">
						{completionTokens.toLocaleString("ru-RU")}
					</dd>

					<dt className="text-slate-500">Сессий в счёте</dt>
					<dd className="font-mono text-slate-900">{sessionsCount}</dd>

					<dt className="text-slate-500">Последний сброс</dt>
					<dd className="text-slate-900">
						{lastResetAt
							? new Date(lastResetAt).toLocaleString("ru-RU")
							: "ещё не было"}
					</dd>
				</dl>
			</div>

			<div className="rounded-lg border border-slate-200 bg-white p-5">
				<h2 className="text-lg font-semibold text-slate-900">Сброс счётчиков</h2>
				<p className="mt-1 text-sm text-slate-600">
					Сброс закрывает текущую сессию учёта активной модели и открывает новую с нулями.
					Данные по другим моделям остаются нетронутыми. История сохраняется в системе
					аудита.
				</p>
				<button
					type="button"
					disabled={!canReset || resetMutation.isPending}
					onClick={() => setShowConfirm(true)}
					className="mt-4 inline-flex items-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
				>
					Сбросить счётчики
				</button>
				{!canReset && modelName && (
					<p className="mt-2 text-xs text-slate-500">
						Счётчики уже на нуле — сбрасывать нечего.
					</p>
				)}
			</div>

			{showConfirm && modelName && (
				<ResetConfirmDialog
					modelName={modelName}
					promptTokens={promptTokens}
					completionTokens={completionTokens}
					onCancel={() => setShowConfirm(false)}
					onConfirm={() => resetMutation.mutate(modelName)}
					isPending={resetMutation.isPending}
				/>
			)}
		</section>
	);
}
